import React, { useState, useEffect, useCallback, useRef } from "react";
import { Card, Row, Col, Form, Button, Alert, Modal, Badge, InputGroup, Spinner } from "react-bootstrap";
import Footer from "../layouts/Footer";
import { fetchSenderList } from "../services/api";
import Select from "react-select";
import axios from "axios";
import { Link } from "react-router-dom";

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error)
);

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('authToken');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export default function DirectMessageSender() {
  // États du formulaire
  const [messageData, setMessageData] = useState({
    texte: "",
    destinataires: "",
    type: "SMS Pro",
    reference: "",
    senderId: "",
    options: {
      accuséReception: false,
      messageLong: false,
      personnalisation: false
    }
  });

  const [showPreview, setShowPreview] = useState(false);
  const [sending, setSending] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [sentMessages, setSentMessages] = useState([]);
  const [error, setError] = useState("");
  const [senderIds, setSenderIds] = useState([]);
  const [loadingSenders, setLoadingSenders] = useState(false);

  const [modeEnvoi, setModeEnvoi] = useState("basic");
  const [templates, setTemplates] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [groupes, setGroupes] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [selectedType, setSelectedType] = useState("");
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [loadingGroupes, setLoadingGroupes] = useState(false);

  // ✅ Ref pour éviter les doubles fetch
  const initDoneRef = useRef(false);

  // Charger les Sender IDs
  useEffect(() => {
    if (initDoneRef.current) return;
    initDoneRef.current = true;

    const loadSenderIds = async () => {
      try {
        setLoadingSenders(true);
        const token = localStorage.getItem('authToken');
        if (!token) return;

        const senders = await fetchSenderList(token);

        const formattedSenders = senders.map(sender => ({
          value: sender.sender_id,
          label: `${sender.sender_id} - ${sender.is_active === "YES" ? "Actif" : "Inactif"}`,
          isActive: sender.is_active === "YES"
        }));

        setSenderIds(formattedSenders);

        const defaultSender = formattedSenders.find(s => s.isActive) || formattedSenders[0];
        if (defaultSender) {
          setMessageData(prev => ({ ...prev, senderId: defaultSender.value }));
        }
      } catch (error) {
        console.error('❌ Erreur Sender ID:', error);
        setError("Erreur lors du chargement des Sender ID");
      } finally {
        setLoadingSenders(false);
      }
    };

    loadSenderIds();
  }, []);

  // Charger les données du mode template
  const loadTemplates = async () => {
    try {
      setLoadingTemplates(true);
      const response = await apiClient.get('/sms-templates');
      const templatesArray = response.data.data || response.data || [];
      setTemplates(templatesArray);
    } catch (error) {
      console.error('❌ Erreur templates:', error);
      setError("Erreur lors du chargement des templates");
    } finally {
      setLoadingTemplates(false);
    }
  };

  const loadContacts = async () => {
    try {
      setLoadingContacts(true);
      const response = await apiClient.get('/contacts');
      const contactsArray = response.data.data || response.data || [];
      setContacts(contactsArray);
    } catch (error) {
      console.error('❌ Erreur contacts:', error);
      setError("Erreur lors du chargement des contacts");
    } finally {
      setLoadingContacts(false);
    }
  };

  const loadGroupes = async () => {
    try {
      setLoadingGroupes(true);
      const response = await apiClient.get('/contact-groups');
      const groupesArray = response.data.data || response.data || [];
      setGroupes(groupesArray);
    } catch (error) {
      console.log('❌ Erreur groupes:', error);
      setError("Aucun groupe trouvé");
    } finally {
      setLoadingGroupes(false);
    }
  };

  useEffect(() => {
    if (modeEnvoi === "template") {
      loadTemplates();
      loadContacts();
      loadGroupes();
    }
  }, [modeEnvoi]);

  // Handlers
  const handleInputChange = (field, value) => {
    setMessageData(prev => ({ ...prev, [field]: value }));
  };

  const handleModeChange = (newMode) => {
    setModeEnvoi(newMode);
    setSelectedTemplate("");
    setSelectedType("");
    setSelectedGroups([]);
    setSelectedContacts([]);
    setMessageData(prev => ({ ...prev, texte: "", destinataires: "" }));
  };

  const handleTemplateChange = (templateId) => {
    setSelectedTemplate(templateId);
    const template = templates.find(t => t.code_template === templateId || t.id === templateId);
    if (template) {
      setMessageData(prev => ({ ...prev, texte: template.content || "" }));
    }
  };

  const handleTypeChange = (type) => {
    setSelectedType(type);
    setSelectedGroups([]);
    setSelectedContacts([]);
    setMessageData(prev => ({ ...prev, destinataires: "" }));
  };

  const getPhoneNumberFromContact = (contact) => {
    if (!contact) return "";
    const fields = ['telephone', 'phone', 'mobile', 'tel', 'numero', 'number', 'contact', 'phone_number', 'msisdn'];
    for (let field of fields) {
      if (contact[field] && contact[field].toString().trim() !== "") {
        return contact[field].toString().trim();
      }
    }
    return "";
  };

  const getRecipientsCount = () => {
    if (modeEnvoi === "template") {
      if (selectedType === "group" && selectedGroups.length > 0) {
        let uniqueContacts = new Set();
        contacts.forEach(contact => {
          if (contact.groupes && contact.id) {
            let contactGroups = [];
            try {
              if (Array.isArray(contact.groupes)) {
                contactGroups = contact.groupes.map(g => String(g).trim());
              } else if (typeof contact.groupes === 'string') {
                try {
                  const parsed = JSON.parse(contact.groupes);
                  contactGroups = Array.isArray(parsed)
                    ? parsed.map(g => String(g).trim())
                    : String(contact.groupes).split(',').map(g => g.trim());
                } catch {
                  contactGroups = String(contact.groupes).split(',').map(g => g.trim());
                }
              } else if (contact.groupes) {
                contactGroups = [String(contact.groupes).trim()];
              }
            } catch (error) {
              console.error('Erreur parsing groupes:', error);
            }

            const hasMatchingGroup = contactGroups.some(groupId =>
              selectedGroups.some(selectedId => String(selectedId) === groupId)
            );

            if (hasMatchingGroup) uniqueContacts.add(contact.id);
          }
        });
        return uniqueContacts.size;
      }

      if (selectedType === "contact" && selectedContacts.length > 0) {
        return selectedContacts.length;
      }
      return 0;
    } else {
      if (!messageData.destinataires || messageData.destinataires.trim() === "") return 0;
      return messageData.destinataires.split(/[,\n]/).filter(r => r.trim()).length;
    }
  };

  const validateForm = () => {
    const hasMessage = messageData.texte && messageData.texte.trim() !== "";
    const hasSenderId = messageData.senderId && messageData.senderId.trim() !== "";

    if (modeEnvoi === "template") {
      const hasTemplate = !!selectedTemplate;
      const hasType = !!selectedType;
      const hasSelection = (selectedType === "group" && selectedGroups.length > 0) ||
                          (selectedType === "contact" && selectedContacts.length > 0);
      return hasMessage && hasSenderId && hasTemplate && hasType && hasSelection;
    }

    const hasRecipients = messageData.destinataires && messageData.destinataires.trim() !== "";
    return hasMessage && hasRecipients && hasSenderId;
  };

  const prepareApiPayload = () => {
    const apiMessageType = messageData.type === "SMS Pro" ? "pro" : "RT";
    const payload = {
      sender_id: messageData.senderId,
      message: messageData.texte,
      type: apiMessageType,
      source: 'bo'
    };

    if (modeEnvoi === "basic") {
      if (messageData.destinataires && messageData.destinataires.trim() !== "") {
        payload.msisdn = messageData.destinataires;
        payload.send_type = "contact";
      }
    } else if (modeEnvoi === "template") {
      if (selectedType === "group" && selectedGroups.length > 0) {
        payload.msisdn = selectedGroups.map(id => id.toString());
        payload.send_type = "group";
      } else if (selectedType === "contact" && selectedContacts.length > 0) {
        const phoneNumbers = selectedContacts.map(contactId => {
          const contact = contacts.find(c => c.id == contactId);
          return getPhoneNumberFromContact(contact);
        }).filter(phone => phone && phone.trim() !== "");

        payload.msisdn = phoneNumbers.map(phone => phone.toString());
        payload.send_type = "contact";
      }
    }

    return payload;
  };

  const sendSMS = async () => {
    const payload = prepareApiPayload();
    const apiUrl = modeEnvoi === "template" ? "/send-from-model" : "/sendsms?v=1";
    const response = await apiClient.post(apiUrl, payload);
    return response.data;
  };

  const sendDirectMessage = async () => {
    if (!validateForm()) {
      alert("Veuillez remplir tous les champs obligatoires");
      return;
    }

    setSending(true);
    setError("");

    try {
      const result = await sendSMS();

      const newMessage = {
        id: Date.now(),
        ...messageData,
        modeEnvoi,
        templateId: selectedTemplate,
        sendType: selectedType,
        groupIds: selectedGroups,
        contactIds: selectedContacts,
        dateEnvoi: new Date().toLocaleString('fr-FR'),
        statut: "Envoyé",
        destinatairesCount: getRecipientsCount(),
        results: [result]
      };

      setSentMessages(prev => [newMessage, ...prev]);
      setShowSuccess(true);

      setMessageData(prev => ({ ...prev, texte: "", destinataires: "", reference: "" }));
      if (modeEnvoi === "template") {
        setSelectedTemplate("");
        setSelectedType("");
        setSelectedGroups([]);
        setSelectedContacts([]);
      }

      setTimeout(() => setShowSuccess(false), 5000);
    } catch (error) {
      console.error('Erreur envoi:', error);
      const errorMsg = error.response?.data?.message || "Une erreur est survenue lors de l'envoi des messages";
      setError(errorMsg);
    } finally {
      setSending(false);
      setShowPreview(false);
      setTimeout(() => setError(""), 5000);
    }
  };

  const recipientsCount = getRecipientsCount();
  const isFormValid = validateForm();
  const characterLimit = messageData.options.messageLong ? 459 : 160;
  const charProgress = Math.min((messageData.texte.length / characterLimit) * 100, 100);

  return (
    <div className="direct-sms main main-app p-3 p-lg-4">
      {/* ========== HEADER ========== */}
      <div className="direct-header mb-4">
        <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
          <div className="d-flex align-items-center gap-3">
            <div className="direct-header-icon">
              <i className="ri-send-plane-fill"></i>
            </div>
            <div>
              <h3 className="direct-header-title mb-1">Envoi direct de SMS</h3>
              <p className="direct-header-subtitle mb-0">
                Envoyez vos messages instantanément via l'API Minerve Tech
              </p>
            </div>
          </div>
          <Button
            className="direct-btn-preview"
            onClick={() => setShowPreview(true)}
            disabled={!isFormValid || sending}
          >
            <i className="ri-eye-line"></i>
            <span>Aperçu</span>
          </Button>
        </div>
      </div>

      {/* ========== ALERTS ========== */}
      {showSuccess && (
        <div className="direct-alert direct-alert-success mb-4">
          <i className="ri-checkbox-circle-fill"></i>
          <div>
            <div className="fw-bold">Message envoyé avec succès</div>
            <div className="small opacity-75">Vos SMS sont en cours d'acheminement</div>
          </div>
        </div>
      )}

      {error && (
        <div className="direct-alert direct-alert-danger mb-4">
          <i className="ri-error-warning-fill"></i>
          <div>
            <div className="fw-bold">Erreur</div>
            <div className="small opacity-75">{error}</div>
          </div>
        </div>
      )}

      <Row className="g-3">
        {/* ========== FORMULAIRE PRINCIPAL ========== */}
        <Col lg={8}>
          <div className="direct-form-card mb-4">
            <div className="direct-form-header">
              <div className="d-flex align-items-center gap-2">
                <div className="direct-form-header-icon">
                  <i className="ri-edit-box-line"></i>
                </div>
                <div>
                  <h6 className="direct-form-title mb-0">Composer un message</h6>
                  <small className="direct-form-subtitle">Remplissez les informations ci-dessous</small>
                </div>
              </div>
            </div>

            <div className="direct-form-body">
              {/* MODE D'ENVOI */}
              <div className="direct-section">
                <label className="direct-label">
                  <span className="direct-label-number">1</span>
                  Mode d'envoi <span className="direct-required">*</span>
                </label>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className={`direct-option-card ${modeEnvoi === "basic" ? 'active' : ''}`}>
                      <input
                        type="radio"
                        name="modeEnvoi"
                        value="basic"
                        checked={modeEnvoi === "basic"}
                        onChange={(e) => handleModeChange(e.target.value)}
                      />
                      <div className="direct-option-icon direct-option-icon-blue">
                        <i className="ri-pencil-line"></i>
                      </div>
                      <div className="direct-option-content">
                        <div className="direct-option-title">Envoi Basic</div>
                        <div className="direct-option-desc">Saisie manuelle du message et des destinataires</div>
                      </div>
                      <div className="direct-option-check">
                        <i className="ri-checkbox-circle-fill"></i>
                      </div>
                    </label>
                  </div>
                  <div className="col-md-6">
                    <label className={`direct-option-card ${modeEnvoi === "template" ? 'active' : ''}`}>
                      <input
                        type="radio"
                        name="modeEnvoi"
                        value="template"
                        checked={modeEnvoi === "template"}
                        onChange={(e) => handleModeChange(e.target.value)}
                      />
                      <div className="direct-option-icon direct-option-icon-green">
                        <i className="ri-file-text-line"></i>
                      </div>
                      <div className="direct-option-content">
                        <div className="direct-option-title">À partir d'un modèle</div>
                        <div className="direct-option-desc">Utilisation de templates et contacts prédéfinis</div>
                      </div>
                      <div className="direct-option-check">
                        <i className="ri-checkbox-circle-fill"></i>
                      </div>
                    </label>
                  </div>
                </div>
              </div>

              {/* SENDER ID */}
              <div className="direct-section">
                <label className="direct-label">
                  <span className="direct-label-number">2</span>
                  Sender ID <span className="direct-required">*</span>
                </label>
                <div className="direct-input-wrapper">
                  <i className="ri-at-line direct-input-icon"></i>
                  <select
                    className="direct-select"
                    value={messageData.senderId}
                    onChange={(e) => handleInputChange('senderId', e.target.value)}
                    disabled={loadingSenders}
                  >
                    <option value="">Sélectionnez un Sender ID</option>
                    {senderIds.map((sender) => (
                      <option key={sender.value} value={sender.value} disabled={!sender.isActive}>
                        {sender.label}
                      </option>
                    ))}
                  </select>
                  {loadingSenders && (
                    <div className="direct-input-spinner">
                      <Spinner animation="border" size="sm" />
                    </div>
                  )}
                </div>
                <div className="direct-hint">
                  <i className="ri-information-line"></i>
                  {messageData.senderId ? `Expéditeur : ${messageData.senderId}` : 'Choisissez le nom d\'expéditeur'}
                </div>
              </div>

              {/* MODE TEMPLATE - Champs spécifiques */}
              {modeEnvoi === "template" && (
                <>
                  {/* MODÈLE */}
                  <div className="direct-section">
                    <label className="direct-label">
                      <span className="direct-label-number">3</span>
                      Modèle de message <span className="direct-required">*</span>
                    </label>
                    <div className="direct-input-wrapper">
                      <i className="ri-file-text-line direct-input-icon"></i>
                      <select
                        className="direct-select"
                        value={selectedTemplate}
                        onChange={(e) => handleTemplateChange(e.target.value)}
                        disabled={loadingTemplates}
                      >
                        <option value="">Sélectionnez un modèle</option>
                        {templates.map((template) => (
                          <option key={template.code_template || template.id} value={template.code_template || template.id}>
                            {template.name}
                          </option>
                        ))}
                      </select>
                      {loadingTemplates && (
                        <div className="direct-input-spinner">
                          <Spinner animation="border" size="sm" />
                        </div>
                      )}
                    </div>
                    <div className="direct-hint">
                      <i className="ri-information-line"></i>
                      {selectedTemplate ? 'Le contenu du modèle sera utilisé' : 'Choisissez un modèle prédéfini'}
                    </div>
                  </div>

                  {/* TYPE D'ENVOI */}
                  <div className="direct-section">
                    <label className="direct-label">
                      <span className="direct-label-number">4</span>
                      Type d'envoi <span className="direct-required">*</span>
                    </label>
                    <div className="row g-3">
                      <div className="col-md-6">
                        <label className={`direct-option-card ${selectedType === "group" ? 'active' : ''}`}>
                          <input
                            type="radio"
                            name="sendType"
                            value="group"
                            checked={selectedType === "group"}
                            onChange={(e) => handleTypeChange(e.target.value)}
                          />
                          <div className="direct-option-icon direct-option-icon-violet">
                            <i className="ri-group-line"></i>
                          </div>
                          <div className="direct-option-content">
                            <div className="direct-option-title">Par groupes</div>
                            <div className="direct-option-desc">Envoyer à des groupes de contacts</div>
                          </div>
                          <div className="direct-option-check">
                            <i className="ri-checkbox-circle-fill"></i>
                          </div>
                        </label>
                      </div>
                      <div className="col-md-6">
                        <label className={`direct-option-card ${selectedType === "contact" ? 'active' : ''}`}>
                          <input
                            type="radio"
                            name="sendType"
                            value="contact"
                            checked={selectedType === "contact"}
                            onChange={(e) => handleTypeChange(e.target.value)}
                          />
                          <div className="direct-option-icon direct-option-icon-amber">
                            <i className="ri-user-3-line"></i>
                          </div>
                          <div className="direct-option-content">
                            <div className="direct-option-title">Par contacts</div>
                            <div className="direct-option-desc">Sélectionner des contacts individuels</div>
                          </div>
                          <div className="direct-option-check">
                            <i className="ri-checkbox-circle-fill"></i>
                          </div>
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* SÉLECTION GROUPES */}
                  {selectedType === "group" && (
                    <div className="direct-section">
                      <label className="direct-label">
                        <span className="direct-label-number">5</span>
                        Groupes <span className="direct-required">*</span>
                      </label>
                      <Select
                        isMulti
                        isDisabled={loadingGroupes}
                        value={groupes
                          .filter(g => selectedGroups.includes(g.id.toString()))
                          .map(g => ({ value: g.id.toString(), label: g.name }))}
                        options={groupes.map(g => ({ value: g.id.toString(), label: g.name }))}
                        onChange={(selected) => setSelectedGroups(selected.map(s => s.value))}
                        placeholder="Sélectionnez un ou plusieurs groupes..."
                        classNamePrefix="react-select"
                        styles={{
                          control: (base) => ({
                            ...base,
                            borderRadius: '10px',
                            borderColor: '#e2e8f0',
                            minHeight: '44px',
                            boxShadow: 'none',
                            '&:hover': { borderColor: '#8b5cf6' },
                          }),
                          multiValue: (base) => ({ ...base, backgroundColor: '#ede9fe' }),
                          multiValueLabel: (base) => ({ ...base, color: '#7c3aed', fontWeight: 600 }),
                          multiValueRemove: (base) => ({
                            ...base,
                            color: '#7c3aed',
                            ':hover': { backgroundColor: '#7c3aed', color: 'white' },
                          }),
                        }}
                      />
                      <div className="direct-hint">
                        <i className="ri-information-line"></i>
                        {selectedGroups.length > 0 ? `${selectedGroups.length} groupe(s) sélectionné(s)` : 'Sélectionnez vos groupes'}
                      </div>
                    </div>
                  )}

                  {/* SÉLECTION CONTACTS */}
                  {selectedType === "contact" && (
                    <div className="direct-section">
                      <label className="direct-label">
                        <span className="direct-label-number">5</span>
                        Contacts <span className="direct-required">*</span>
                      </label>
                      <Select
                        isMulti
                        isDisabled={loadingContacts}
                        value={contacts
                          .filter(c => selectedContacts.includes(c.id.toString()))
                          .map(c => ({
                            value: c.id.toString(),
                            label: `${c.prenom} ${c.nom} - ${getPhoneNumberFromContact(c) || 'Pas de téléphone'}`,
                          }))}
                        options={contacts.map(c => ({
                          value: c.id.toString(),
                          label: `${c.prenom} ${c.nom} - ${getPhoneNumberFromContact(c) || 'Pas de téléphone'}`,
                        }))}
                        onChange={(selected) => setSelectedContacts(selected.map(s => s.value))}
                        placeholder="Sélectionnez un ou plusieurs contacts..."
                        classNamePrefix="react-select"
                        styles={{
                          control: (base) => ({
                            ...base,
                            borderRadius: '10px',
                            borderColor: '#e2e8f0',
                            minHeight: '44px',
                            boxShadow: 'none',
                            '&:hover': { borderColor: '#f59e0b' },
                          }),
                          multiValue: (base) => ({ ...base, backgroundColor: '#fef3c7' }),
                          multiValueLabel: (base) => ({ ...base, color: '#d97706', fontWeight: 600 }),
                          multiValueRemove: (base) => ({
                            ...base,
                            color: '#d97706',
                            ':hover': { backgroundColor: '#d97706', color: 'white' },
                          }),
                        }}
                      />
                      <div className="direct-hint">
                        <i className="ri-information-line"></i>
                        {selectedContacts.length > 0 ? `${selectedContacts.length} contact(s) sélectionné(s)` : 'Sélectionnez vos contacts'}
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* DESTINATAIRES MODE BASIC */}
              {modeEnvoi === "basic" && (
                <div className="direct-section">
                  <label className="direct-label">
                    <span className="direct-label-number">3</span>
                    Destinataires <span className="direct-required">*</span>
                  </label>
                  <div className="direct-input-wrapper">
                    <i className="ri-phone-line direct-input-icon"></i>
                    <input
                      type="text"
                      className="direct-input direct-input-with-counter"
                      placeholder="Ex: 24177000000, 24106000000"
                      value={messageData.destinataires}
                      onChange={(e) => handleInputChange('destinataires', e.target.value)}
                    />
                    {recipientsCount > 0 && (
                      <div className="direct-input-counter direct-input-counter-primary">
                        {recipientsCount} <i className="ri-user-line"></i>
                      </div>
                    )}
                  </div>
                  <div className="direct-hint">
                    <i className="ri-information-line"></i>
                    Séparez les numéros par des virgules
                  </div>
                </div>
              )}

              {/* CONTENU DU MESSAGE */}
              <div className="direct-section">
                <label className="direct-label">
                  <span className="direct-label-number">{modeEnvoi === "basic" ? "4" : "6"}</span>
                  Contenu du message <span className="direct-required">*</span>
                </label>
                <textarea
                  className="direct-textarea"
                  rows={5}
                  placeholder={
                    modeEnvoi === "template" && !selectedTemplate
                      ? "Sélectionnez d'abord un modèle..."
                      : "Rédigez votre message SMS ici..."
                  }
                  value={messageData.texte}
                  onChange={(e) => handleInputChange('texte', e.target.value)}
                  maxLength={characterLimit}
                />
                <div className="direct-textarea-footer">
                  <div className="direct-char-counter">
                    <div className="direct-char-progress">
                      <div
                        className={`direct-char-progress-bar ${charProgress > 80 ? 'warning' : ''}`}
                        style={{ width: `${charProgress}%` }}
                      ></div>
                    </div>
                    <span className={`direct-char-text ${charProgress > 80 ? 'warning' : ''}`}>
                      {messageData.texte.length} / {characterLimit}
                    </span>
                  </div>
                  {messageData.texte.length > 160 && !messageData.options.messageLong && (
                    <div className="direct-warning">
                      <i className="ri-alert-line"></i>
                      Le message sera divisé en plusieurs SMS
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </Col>

        {/* ========== PANNEAU LATÉRAL ========== */}
        <Col lg={4}>
          <div className="direct-sidebar sticky-top" style={{ top: '80px', zIndex: 1 }}>
            <div className="direct-summary-card">
              <div className="direct-summary-header">
                <div className="d-flex align-items-center gap-2">
                  <div className="direct-summary-header-icon">
                    <i className="ri-send-plane-fill"></i>
                  </div>
                  <div>
                    <h6 className="direct-summary-title mb-0">Récapitulatif</h6>
                    <small className="direct-summary-subtitle">Vérifiez avant l'envoi</small>
                  </div>
                </div>
              </div>

              <div className="direct-summary-body">
                <div className="direct-summary-item">
                  <span className="direct-summary-label">
                    <i className="ri-settings-3-line"></i>
                    Mode
                  </span>
                  <span className={`direct-summary-badge ${modeEnvoi === "basic" ? "blue" : "green"}`}>
                    {modeEnvoi === "basic" ? "Basic" : "Template"}
                  </span>
                </div>

                <div className="direct-summary-item">
                  <span className="direct-summary-label">
                    <i className="ri-price-tag-3-line"></i>
                    Type
                  </span>
                  <span className={`direct-summary-badge ${messageData.type === "SMS Pro" ? "amber" : "blue"}`}>
                    {messageData.type}
                  </span>
                </div>

                <div className="direct-summary-item">
                  <span className="direct-summary-label">
                    <i className="ri-at-line"></i>
                    Sender
                  </span>
                  <span className="direct-summary-value">
                    {messageData.senderId || <span className="text-muted">—</span>}
                  </span>
                </div>

                <div className="direct-summary-item">
                  <span className="direct-summary-label">
                    <i className="ri-user-line"></i>
                    Destinataires
                  </span>
                  <span className="direct-summary-value direct-summary-value-highlight">
                    {recipientsCount}
                  </span>
                </div>

                <div className="direct-summary-item">
                  <span className="direct-summary-label">
                    <i className="ri-text"></i>
                    Caractères
                  </span>
                  <span className={`direct-summary-value ${messageData.texte.length > 160 ? 'text-warning' : ''}`}>
                    {messageData.texte.length}
                  </span>
                </div>

                {modeEnvoi === "template" && (
                  <>
                    <div className="direct-summary-item">
                      <span className="direct-summary-label">
                        <i className="ri-file-text-line"></i>
                        Modèle
                      </span>
                      <span className={`direct-summary-badge ${selectedTemplate ? "green" : "gray"}`}>
                        {selectedTemplate ? "Utilisé" : "Non utilisé"}
                      </span>
                    </div>
                    <div className="direct-summary-item">
                      <span className="direct-summary-label">
                        <i className="ri-group-line"></i>
                        Type d'envoi
                      </span>
                      <span className={`direct-summary-badge ${selectedType === "group" ? "violet" : selectedType === "contact" ? "amber" : "gray"}`}>
                        {selectedType === "group" ? "Groupes" : selectedType === "contact" ? "Contacts" : "—"}
                      </span>
                    </div>
                    {selectedType === "group" && (
                      <div className="direct-summary-item">
                        <span className="direct-summary-label">
                          <i className="ri-stack-line"></i>
                          Groupes
                        </span>
                        <span className="direct-summary-value direct-summary-value-violet">
                          {selectedGroups.length}
                        </span>
                      </div>
                    )}
                    {selectedType === "contact" && (
                      <div className="direct-summary-item">
                        <span className="direct-summary-label">
                          <i className="ri-contacts-line"></i>
                          Contacts
                        </span>
                        <span className="direct-summary-value direct-summary-value-amber">
                          {selectedContacts.length}
                        </span>
                      </div>
                    )}
                  </>
                )}
              </div>

              <div className="direct-summary-footer">
                <button
                  className="direct-btn-secondary w-100"
                  onClick={() => setShowPreview(true)}
                  disabled={!isFormValid || sending}
                >
                  <i className="ri-eye-line"></i>
                  Aperçu du message
                </button>

                <button
                  className="direct-btn-primary w-100"
                  onClick={sendDirectMessage}
                  disabled={!isFormValid || sending}
                >
                  {sending ? (
                    <>
                      <Spinner animation="border" size="sm" />
                      Envoi en cours...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-fill"></i>
                      Envoyer maintenant
                    </>
                  )}
                </button>

                <div className="direct-summary-hint">
                  <i className="ri-flashlight-line"></i>
                  {modeEnvoi === "template"
                    ? "Envoi via /send-from-model"
                    : "Envoi via /sendsms"}
                </div>
              </div>
            </div>
          </div>
        </Col>
      </Row>

      {/* ========== MODAL APERÇU ========== */}
      <Modal
        show={showPreview}
        onHide={() => setShowPreview(false)}
        size="lg"
        centered
        className="direct-modal"
      >
        <div className="direct-modal-header">
          <div className="d-flex align-items-center gap-3">
            <div className="direct-modal-header-icon">
              <i className="ri-eye-line"></i>
            </div>
            <div>
              <h5 className="direct-modal-title mb-0">Aperçu du message</h5>
              <small className="direct-modal-subtitle">Vérifiez avant l'envoi</small>
            </div>
          </div>
          <button className="direct-modal-close" onClick={() => setShowPreview(false)}>
            <i className="ri-close-line"></i>
          </button>
        </div>

        <Modal.Body className="direct-modal-body">
          {/* Phone mockup */}
          <div className="direct-phone-mockup">
            <div className="direct-phone-header">
              <div className="direct-phone-notch"></div>
              <div className="direct-phone-sender">
                <div className="direct-phone-avatar">
                  <i className="ri-user-fill"></i>
                </div>
                <div>
                  <div className="direct-phone-name">{messageData.senderId || "Expéditeur"}</div>
                  <div className="direct-phone-meta">SMS • Maintenant</div>
                </div>
              </div>
            </div>

            <div className="direct-phone-body">
              <div className="direct-sms-bubble">
                <div className="direct-sms-content">
                  {messageData.texte || "Votre message apparaîtra ici..."}
                </div>
                <div className="direct-sms-meta">
                  {messageData.texte.length} caractères
                </div>
              </div>
            </div>
          </div>

          {/* Détails */}
          <div className="direct-preview-details">
            <div className="direct-preview-details-header">
              <i className="ri-information-line"></i>
              Détails de l'envoi
            </div>

            <div className="row g-3">
              <div className="col-md-6">
                <div className="direct-preview-detail">
                  <div className="direct-preview-detail-label">Sender ID</div>
                  <div className="direct-preview-detail-value">{messageData.senderId || "—"}</div>
                </div>
              </div>
              <div className="col-md-6">
                <div className="direct-preview-detail">
                  <div className="direct-preview-detail-label">Destinataires</div>
                  <div className="direct-preview-detail-value direct-preview-detail-value-primary">
                    {recipientsCount}
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <div className="direct-preview-detail">
                  <div className="direct-preview-detail-label">Type</div>
                  <div className="direct-preview-detail-value">{messageData.type}</div>
                </div>
              </div>
              <div className="col-md-6">
                <div className="direct-preview-detail">
                  <div className="direct-preview-detail-label">Mode</div>
                  <div className="direct-preview-detail-value">
                    {modeEnvoi === "basic" ? "Basic" : "Template"}
                  </div>
                </div>
              </div>
            </div>

            {/* Destinataires détails */}
            {modeEnvoi === "basic" && messageData.destinataires && (
              <div className="direct-preview-recipients">
                <div className="direct-preview-recipients-label">
                  Destinataires ({recipientsCount}) :
                </div>
                <div className="direct-preview-recipients-list">
                  {messageData.destinataires.split(/[,\n]/).slice(0, 5).map((dest, i) => dest.trim() && (
                    <div key={i} className="direct-preview-recipient">
                      <i className="ri-phone-line"></i>
                      {dest.trim()}
                    </div>
                  ))}
                  {recipientsCount > 5 && (
                    <div className="direct-preview-recipient direct-preview-recipient-more">
                      +{recipientsCount - 5} autres...
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </Modal.Body>

        <div className="direct-modal-footer">
          <button className="direct-btn-cancel" onClick={() => setShowPreview(false)}>
            <i className="ri-arrow-left-line"></i>
            Retour
          </button>
          <button
            className="direct-btn-primary"
            onClick={() => { setShowPreview(false); sendDirectMessage(); }}
            disabled={!isFormValid || sending}
          >
            {sending ? (
              <>
                <Spinner animation="border" size="sm" />
                Envoi...
              </>
            ) : (
              <>
                <i className="ri-send-plane-fill"></i>
                Envoyer
              </>
            )}
          </button>
        </div>
      </Modal>

      <Footer />

      {/* ========== STYLES PREMIUM ========== */}
      <style>{`
        /* ========== GLOBAL ========== */
        .direct-sms {
          background: #f8fafc;
          min-height: 100vh;
        }
        .direct-sms * {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        }

        /* ========== HEADER ========== */
        .direct-header {
          background: linear-gradient(135deg, #ffffff 0%, #f1f5f9 100%);
          border-radius: 16px;
          padding: 24px 28px;
          border: 1px solid #e2e8f0;
          box-shadow: 0 1px 3px rgba(0,0,0,0.03);
        }
        .direct-header-icon {
          width: 56px;
          height: 56px;
          border-radius: 14px;
          background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 26px;
          box-shadow: 0 8px 20px -6px rgba(59, 130, 246, 0.5);
        }
        .direct-header-title {
          font-weight: 700;
          color: #0f172a;
          font-size: 1.5rem;
          letter-spacing: -0.02em;
        }
        .direct-header-subtitle {
          color: #64748b;
          font-size: 0.875rem;
        }
        .direct-btn-preview {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 10px 18px;
          border-radius: 10px;
          font-weight: 600;
          font-size: 0.875rem;
          border: 1px solid #e2e8f0;
          background: white;
          color: #334155;
          transition: all 0.2s;
        }
        .direct-btn-preview:hover:not(:disabled) {
          background: #f8fafc;
          border-color: #3b82f6;
          color: #3b82f6;
          transform: translateY(-1px);
        }
        .direct-btn-preview:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        /* ========== ALERTS ========== */
        .direct-alert {
          display: flex;
          align-items: center;
          gap: 14px;
          padding: 16px 20px;
          border-radius: 12px;
          border: none;
          font-size: 0.875rem;
        }
        .direct-alert > i {
          font-size: 1.5rem;
          flex-shrink: 0;
        }
        .direct-alert-success {
          background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
          color: #065f46;
          border-left: 4px solid #10b981;
        }
        .direct-alert-success > i {
          color: #10b981;
        }
        .direct-alert-danger {
          background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
          color: #991b1b;
          border-left: 4px solid #ef4444;
        }
        .direct-alert-danger > i {
          color: #ef4444;
        }

        /* ========== FORM CARD ========== */
        .direct-form-card {
          background: white;
          border-radius: 16px;
          border: 1px solid #e2e8f0;
          overflow: hidden;
          box-shadow: 0 1px 3px rgba(0,0,0,0.03);
        }
        .direct-form-header {
          padding: 20px 24px;
          background: linear-gradient(180deg, #ffffff, #fafbfc);
          border-bottom: 1px solid #f1f5f9;
        }
        .direct-form-header-icon {
          width: 40px;
          height: 40px;
          border-radius: 10px;
          background: #eff6ff;
          color: #3b82f6;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 18px;
        }
        .direct-form-title {
          font-weight: 700;
          color: #0f172a;
          font-size: 0.95rem;
          margin: 0;
        }
        .direct-form-subtitle {
          color: #94a3b8;
          font-size: 0.75rem;
        }
        .direct-form-body {
          padding: 24px;
        }

        /* ========== SECTIONS ========== */
        .direct-section {
          margin-bottom: 28px;
        }
        .direct-section:last-child {
          margin-bottom: 0;
        }
        .direct-label {
          display: flex;
          align-items: center;
          gap: 10px;
          font-weight: 700;
          color: #0f172a;
          font-size: 0.875rem;
          margin-bottom: 12px;
        }
        .direct-label-number {
          width: 24px;
          height: 24px;
          border-radius: 50%;
          background: linear-gradient(135deg, #3b82f6, #8b5cf6);
          color: white;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 0.7rem;
          font-weight: 700;
        }
        .direct-required {
          color: #ef4444;
        }

        /* ========== OPTION CARDS ========== */
        .direct-option-card {
          position: relative;
          display: flex;
          align-items: center;
          gap: 14px;
          padding: 16px;
          border-radius: 12px;
          border: 2px solid #e2e8f0;
          background: #f8fafc;
          cursor: pointer;
          transition: all 0.2s;
          margin: 0;
          height: 100%;
        }
        .direct-option-card:hover {
          background: white;
          border-color: #cbd5e1;
          transform: translateY(-2px);
          box-shadow: 0 8px 20px -6px rgba(0,0,0,0.06);
        }
        .direct-option-card.active {
          background: white;
          border-color: #3b82f6;
          box-shadow: 0 8px 20px -6px rgba(59, 130, 246, 0.2);
        }
        .direct-option-card input[type="radio"] {
          display: none;
        }
        .direct-option-icon {
          width: 44px;
          height: 44px;
          border-radius: 11px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
          flex-shrink: 0;
        }
        .direct-option-icon-blue { background: #eff6ff; color: #3b82f6; }
        .direct-option-icon-green { background: #d1fae5; color: #10b981; }
        .direct-option-icon-violet { background: #ede9fe; color: #8b5cf6; }
        .direct-option-icon-amber { background: #fef3c7; color: #f59e0b; }
        .direct-option-content {
          flex-grow: 1;
        }
        .direct-option-title {
          font-weight: 700;
          color: #0f172a;
          font-size: 0.875rem;
          margin-bottom: 2px;
        }
        .direct-option-desc {
          font-size: 0.75rem;
          color: #64748b;
        }
        .direct-option-check {
          color: #cbd5e1;
          font-size: 1.2rem;
          transition: all 0.2s;
          flex-shrink: 0;
        }
        .direct-option-card.active .direct-option-check {
          color: #3b82f6;
        }

        /* ========== INPUTS ========== */
        .direct-input-wrapper {
          position: relative;
          display: flex;
          align-items: center;
        }
        .direct-input-icon {
          position: absolute;
          left: 16px;
          color: #94a3b8;
          font-size: 1.05rem;
          pointer-events: none;
          z-index: 5;
        }
        .direct-input, .direct-select {
          width: 100%;
          padding: 12px 16px 12px 46px;
          border: 2px solid #e2e8f0;
          border-radius: 10px;
          font-size: 0.875rem;
          color: #0f172a;
          background: white;
          transition: all 0.2s;
          outline: none;
          font-family: inherit;
        }
        .direct-select {
          appearance: none;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%2394a3b8'%3E%3Cpath d='M12 16L6 10H18L12 16Z'%3E%3C/path%3E%3C/svg%3E");
          background-repeat: no-repeat;
          background-position: right 12px center;
          background-size: 20px;
          cursor: pointer;
        }
        .direct-input:focus, .direct-select:focus {
          border-color: #3b82f6;
          box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1);
        }
        .direct-input-with-counter {
          padding-right: 90px;
        }
        .direct-input-counter {
          position: absolute;
          right: 12px;
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 4px 10px;
          border-radius: 6px;
          font-size: 0.75rem;
          font-weight: 700;
        }
        .direct-input-counter-primary {
          background: #eff6ff;
          color: #3b82f6;
        }
        .direct-input-spinner {
          position: absolute;
          right: 46px;
        }
        .direct-hint {
          margin-top: 8px;
          font-size: 0.75rem;
          color: #94a3b8;
          display: flex;
          align-items: center;
          gap: 5px;
        }
        .direct-hint i {
          color: #3b82f6;
        }

        /* ========== TEXTAREA ========== */
        .direct-textarea {
          width: 100%;
          padding: 16px;
          border: 2px solid #e2e8f0;
          border-radius: 10px;
          font-size: 0.95rem;
          color: #0f172a;
          background: white;
          transition: all 0.2s;
          outline: none;
          font-family: inherit;
          line-height: 1.5;
          resize: vertical;
        }
        .direct-textarea:focus {
          border-color: #3b82f6;
          box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1);
        }
        .direct-textarea-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-top: 10px;
          gap: 16px;
          flex-wrap: wrap;
        }
        .direct-char-counter {
          display: flex;
          align-items: center;
          gap: 10px;
          flex-grow: 1;
          max-width: 300px;
        }
        .direct-char-progress {
          flex-grow: 1;
          height: 6px;
          background: #f1f5f9;
          border-radius: 10px;
          overflow: hidden;
        }
        .direct-char-progress-bar {
          height: 100%;
          background: linear-gradient(90deg, #3b82f6, #8b5cf6);
          border-radius: 10px;
          transition: width 0.3s ease;
        }
        .direct-char-progress-bar.warning {
          background: linear-gradient(90deg, #f59e0b, #ef4444);
        }
        .direct-char-text {
          font-size: 0.75rem;
          color: #64748b;
          font-weight: 600;
          font-variant-numeric: tabular-nums;
          white-space: nowrap;
        }
        .direct-char-text.warning {
          color: #f59e0b;
        }
        .direct-warning {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          font-size: 0.75rem;
          color: #f59e0b;
          font-weight: 600;
        }

        /* ========== SIDEBAR ========== */
        .direct-sidebar {
          position: sticky;
        }
        .direct-summary-card {
          background: white;
          border-radius: 16px;
          border: 1px solid #e2e8f0;
          overflow: hidden;
          box-shadow: 0 4px 12px -4px rgba(0,0,0,0.05);
        }
        .direct-summary-header {
          padding: 20px 24px;
          background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
          color: white;
        }
        .direct-summary-header-icon {
          width: 40px;
          height: 40px;
          border-radius: 10px;
          background: rgba(59, 130, 246, 0.2);
          color: #60a5fa;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 18px;
        }
        .direct-summary-title {
          font-weight: 700;
          color: white;
          margin: 0;
          font-size: 0.95rem;
        }
        .direct-summary-subtitle {
          color: #94a3b8;
          font-size: 0.72rem;
        }
        .direct-summary-body {
          padding: 20px 24px;
        }
        .direct-summary-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 12px 0;
          border-bottom: 1px solid #f1f5f9;
        }
        .direct-summary-item:last-child {
          border-bottom: none;
        }
        .direct-summary-label {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          font-size: 0.8rem;
          color: #64748b;
          font-weight: 500;
        }
        .direct-summary-label i {
          color: #94a3b8;
          font-size: 0.95rem;
        }
        .direct-summary-value {
          font-weight: 700;
          color: #0f172a;
          font-size: 0.875rem;
        }
        .direct-summary-value-highlight {
          color: #3b82f6;
          font-size: 1.1rem;
        }
        .direct-summary-value-violet {
          color: #8b5cf6;
          font-size: 1rem;
        }
        .direct-summary-value-amber {
          color: #f59e0b;
          font-size: 1rem;
        }
        .direct-summary-badge {
          display: inline-block;
          padding: 4px 10px;
          border-radius: 6px;
          font-size: 0.7rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.03em;
        }
        .direct-summary-badge.blue   { background: #eff6ff; color: #3b82f6; }
        .direct-summary-badge.green  { background: #d1fae5; color: #059669; }
        .direct-summary-badge.violet { background: #ede9fe; color: #7c3aed; }
        .direct-summary-badge.amber  { background: #fef3c7; color: #d97706; }
        .direct-summary-badge.gray   { background: #f1f5f9; color: #64748b; }
        .direct-summary-footer {
          padding: 20px 24px;
          border-top: 1px solid #f1f5f9;
          display: flex;
          flex-direction: column;
          gap: 10px;
        }
        .direct-btn-primary, .direct-btn-secondary {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 12px 18px;
          border-radius: 10px;
          font-weight: 600;
          font-size: 0.875rem;
          border: none;
          transition: all 0.2s;
          cursor: pointer;
        }
        .direct-btn-primary {
          background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
          color: white;
        }
        .direct-btn-primary:hover:not(:disabled) {
          background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
          transform: translateY(-1px);
          box-shadow: 0 8px 16px -4px rgba(59, 130, 246, 0.4);
        }
        .direct-btn-primary:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .direct-btn-secondary {
          background: white;
          border: 1px solid #e2e8f0;
          color: #334155;
        }
        .direct-btn-secondary:hover:not(:disabled) {
          background: #f8fafc;
          border-color: #cbd5e1;
          color: #0f172a;
        }
        .direct-btn-secondary:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .direct-summary-hint {
          text-align: center;
          font-size: 0.72rem;
          color: #94a3b8;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 5px;
          margin-top: 4px;
        }

        /* ========== MODAL ========== */
        .direct-modal .modal-content {
          border: none;
          border-radius: 16px;
          overflow: hidden;
        }
        .direct-modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px 24px;
          background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
          color: white;
        }
        .direct-modal-header-icon {
          width: 44px;
          height: 44px;
          border-radius: 10px;
          background: rgba(255,255,255,0.1);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
        }
        .direct-modal-title {
          font-weight: 700;
          letter-spacing: -0.01em;
        }
        .direct-modal-subtitle {
          color: #94a3b8;
          font-size: 0.8rem;
        }
        .direct-modal-close {
          background: rgba(255,255,255,0.1);
          border: none;
          color: white;
          width: 36px;
          height: 36px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s;
          font-size: 1.1rem;
        }
        .direct-modal-close:hover {
          background: rgba(255,255,255,0.2);
          transform: rotate(90deg);
        }
        .direct-modal-body {
          padding: 24px;
          background: #f8fafc;
          max-height: 65vh;
          overflow-y: auto;
        }
        .direct-modal-footer {
          padding: 16px 24px;
          background: white;
          border-top: 1px solid #e2e8f0;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .direct-btn-cancel {
          background: white;
          border: 1px solid #e2e8f0;
          color: #334155;
          padding: 10px 18px;
          border-radius: 10px;
          font-weight: 600;
          font-size: 0.875rem;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          cursor: pointer;
          transition: all 0.2s;
        }
        .direct-btn-cancel:hover {
          background: #f1f5f9;
          color: #0f172a;
        }

        /* Phone Mockup */
        .direct-phone-mockup {
          max-width: 360px;
          margin: 0 auto 24px;
          background: white;
          border-radius: 24px;
          border: 8px solid #0f172a;
          overflow: hidden;
          box-shadow: 0 20px 40px -12px rgba(0,0,0,0.15);
        }
        .direct-phone-header {
          background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
          padding: 12px 16px;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 10px;
        }
        .direct-phone-notch {
          width: 80px;
          height: 4px;
          background: rgba(255,255,255,0.3);
          border-radius: 10px;
        }
        .direct-phone-sender {
          display: flex;
          align-items: center;
          gap: 10px;
          width: 100%;
        }
        .direct-phone-avatar {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          background: linear-gradient(135deg, #3b82f6, #8b5cf6);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 16px;
          flex-shrink: 0;
        }
        .direct-phone-name {
          color: white;
          font-weight: 600;
          font-size: 0.85rem;
        }
        .direct-phone-meta {
          color: #94a3b8;
          font-size: 0.7rem;
        }
        .direct-phone-body {
          padding: 16px;
          background: #f8fafc;
          min-height: 200px;
        }
        .direct-sms-bubble {
          background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
          color: white;
          border-radius: 16px 16px 16px 4px;
          padding: 12px 16px;
          max-width: 90%;
          box-shadow: 0 4px 12px -4px rgba(59, 130, 246, 0.3);
        }
        .direct-sms-content {
          font-size: 0.875rem;
          line-height: 1.5;
          white-space: pre-wrap;
          word-break: break-word;
        }
        .direct-sms-meta {
          font-size: 0.65rem;
          color: rgba(255,255,255,0.7);
          margin-top: 6px;
          text-align: right;
        }

        /* Preview Details */
        .direct-preview-details {
          background: white;
          border-radius: 12px;
          padding: 20px;
          border: 1px solid #e2e8f0;
        }
        .direct-preview-details-header {
          display: flex;
          align-items: center;
          gap: 8px;
          font-weight: 700;
          color: #0f172a;
          font-size: 0.9rem;
          margin-bottom: 16px;
          padding-bottom: 12px;
          border-bottom: 1px solid #f1f5f9;
        }
        .direct-preview-details-header i {
          color: #3b82f6;
        }
        .direct-preview-detail {
          background: #f8fafc;
          padding: 12px 14px;
          border-radius: 10px;
        }
        .direct-preview-detail-label {
          font-size: 0.7rem;
          color: #64748b;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          margin-bottom: 4px;
        }
        .direct-preview-detail-value {
          font-weight: 700;
          color: #0f172a;
          font-size: 0.875rem;
        }
        .direct-preview-detail-value-primary {
          color: #3b82f6;
          font-size: 1.05rem;
        }
        .direct-preview-recipients {
          margin-top: 16px;
          padding-top: 16px;
          border-top: 1px solid #f1f5f9;
        }
        .direct-preview-recipients-label {
          font-size: 0.75rem;
          color: #64748b;
          font-weight: 600;
          margin-bottom: 8px;
        }
        .direct-preview-recipients-list {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }
        .direct-preview-recipient {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 12px;
          background: #f8fafc;
          border-radius: 8px;
          font-size: 0.8rem;
          color: #334155;
          font-family: 'SF Mono', Menlo, Monaco, monospace;
        }
        .direct-preview-recipient i {
          color: #3b82f6;
          font-size: 0.9rem;
        }
        .direct-preview-recipient-more {
          justify-content: center;
          color: #94a3b8;
          font-style: italic;
          font-family: inherit;
        }

        /* ========== RESPONSIVE ========== */
        @media (max-width: 768px) {
          .direct-header {
            padding: 20px;
          }
          .direct-header-title {
            font-size: 1.2rem;
          }
          .direct-form-body {
            padding: 16px;
          }
          .direct-modal-body {
            padding: 16px;
          }
          .direct-option-card {
            flex-direction: column;
            text-align: center;
          }
        }
      `}</style>
    </div>
  );
}